import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { formatCurrencyValue } from '../../global/helpers';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import type { NullOr } from '../../global/utils.type';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UICurrency
 * @element ui-currency
 * @classdesc Represents a class for <code>ui-currency</code> element.
 * Formats currency value.
 * @property {number} [value] {@attr value} raw value
 * @property {string} [separator] {@attr separator} separate groups of thousands
 * @property {number} [accuracy] {@attr accuracy} amount of decimals numbers after comma
 * @example
 * <ui-currency value="12345678.98"></ui-currency
 * @example
 * <ui-currency value="12345678.98" accuracy="0" separator=","></ui-currency>
 */
@Component({ tag: 'ui-currency' })
export class UICurrency extends UIElement {
    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['value', 'separator', 'accuracy'];
    }

    @Attribute(Number) value?: number;
    @Attribute(String) separator?: string;
    @Attribute(Number) accuracy: number = 2;

    updateContent(): void {
        const formattedCurrencyValue = formatCurrencyValue(
            this.value!,
            this.separator || ' ',
            this.accuracy
        );
        this.setInnerText(formattedCurrencyValue!);
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }
        this.updateContent();
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.updateContent();
    }
}
